import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface TruthGateProps {
  /**
   * The backing value. If `null` or `undefined`, the gate renders the skeleton.
   * Per the Truth Model (mem://business/marketing-governance-and-incentives-v1),
   * every visible quantitative claim on the landing page must come from a real
   * query — never a hardcoded literal.
   */
  value: unknown;
  /** Whether the backing query is loading. */
  loading?: boolean;
  /** Render prop receiving the resolved (non-null) value. */
  children: (value: NonNullable<unknown>) => ReactNode;
  /** Optional skeleton override. */
  fallback?: ReactNode;
  /** Width of the default skeleton (px). */
  skeletonWidth?: number;
  /** Optional class for the skeleton wrapper. */
  className?: string;
}

/**
 * `<TruthGate>` — gate for queryable telemetry on the v2 landing.
 *
 * Renders `children(value)` only when `value` is non-null and not loading;
 * otherwise renders an inline skeleton. This guarantees that no hardcoded
 * number ever appears in a Truth-Model-protected surface, because every
 * such surface must resolve real data before it paints.
 */
export function TruthGate({
  value,
  loading = false,
  children,
  fallback,
  skeletonWidth = 56,
  className,
}: TruthGateProps) {
  const isReady = !loading && value !== null && value !== undefined;

  if (!isReady) {
    if (fallback) return <>{fallback}</>;
    return (
      <span
        aria-busy="true"
        aria-live="polite"
        className={cn(
          "mono-meta inline-block h-[1em] rounded-sm bg-muted/60 align-baseline animate-pulse",
          className
        )}
        style={{ width: skeletonWidth }}
      />
    );
  }

  return <>{children(value as NonNullable<unknown>)}</>;
}
